import { STORAGE_KEYS } from '@/lib/configs/storage-config'
import { namespacedKey } from '@/lib/safe-storage'
import type { ChatThreadMeta } from '@/state/chat'
import { isResearchJobActive, type ResearchJob } from '@/state/research'

/**
 * Just enough to pick a research run back up after a reload: the dock shows it again, the
 * poller follows it, and the notifier announces it when it lands, even if it landed while the
 * page was closed. The conversation itself is never stored here; it comes from the server's
 * thread history. Per-user key (namespacedKey), bounded to runs that are live or unannounced.
 */
export interface StoredResearchJob {
  id: string
  jobId: string
  threadId: string
  serverThreadId?: string
  thread?: Pick<ChatThreadMeta, 'surfaceId' | 'cardId' | 'pageId' | 'source' | 'title'>
  kind: ResearchJob['kind']
  parentJobId?: string
  rootJobId?: string
  question: string
  assistantMessageId: string
  startedAt: number
}

const MAX_STORED = 20
const MAX_AGE_MS = 24 * 60 * 60 * 1000

const storageKey = (userId: string) =>
  namespacedKey(STORAGE_KEYS.dashboard.researchActiveJobs, userId)

/** Live runs, plus finished ones whose notification hasn't fired yet. */
export function shouldStoreResearchJob(job: ResearchJob) {
  return !!job.jobId && (isResearchJobActive(job) || !job.notifiedAt)
}

export function readStoredResearchJobs(userId: string, now = Date.now()): StoredResearchJob[] {
  try {
    const raw = window.localStorage.getItem(storageKey(userId))
    const parsed: unknown = raw ? JSON.parse(raw) : []
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (entry): entry is StoredResearchJob =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof entry.id === 'string' &&
        typeof entry.jobId === 'string' &&
        typeof entry.threadId === 'string' &&
        typeof entry.assistantMessageId === 'string' &&
        typeof entry.startedAt === 'number' &&
        now - entry.startedAt < MAX_AGE_MS
    )
  } catch {
    return []
  }
}

export function writeStoredResearchJobs(userId: string, jobs: StoredResearchJob[]) {
  try {
    const key = storageKey(userId)
    if (jobs.length === 0) window.localStorage.removeItem(key)
    else window.localStorage.setItem(key, JSON.stringify(jobs.slice(-MAX_STORED)))
  } catch {
    // Storage blocked/full: the run still works this session, it just won't survive a reload.
  }
}
